<template>
  <b-card class="versus-overview-card d-flex flex-column">
    <div>
      <b-card-title class="mb-0">
        {{ title }}
      </b-card-title>
      <div class="font-small-3 mb-1 text-muted">
        {{ subtitle }}
      </div>
    </div>
    <!-- apex chart -->
    <loading-container v-if="loading" />
    <template v-else-if="hasData && chartSeries.length > 0">
      <div class="mt-auto">
        <div class="d-flex justify-content-between mb-1">
          <b-media
            v-for="(item, index) in itemLabels"
            :key="index"
            no-body
            :class="index === 0 ? '' : 'justify-content-end'"
            class="clickable-item"
            @click="handleItemClick(item)"
          >
            <b-media-aside
              v-if="index === 0"
              class="mr-1"
            >
              <b-avatar
                size="38"
                :variant="item.color"
                rounded
              >
                <feather-icon
                  size="20"
                  :icon="item.icon"
                />
              </b-avatar>
            </b-media-aside>
            <b-media-body class="my-auto" :class="index === 1 ? 'text-right' : ''">
              <h4 class="font-weight-bolder mb-0">
                {{ item.count }}
              </h4>
              <b-card-text class="font-small-3 mb-0 text-capitalize">
                {{ item.name }}
              </b-card-text>
            </b-media-body>
            <b-media-aside
              v-if="index === 1"
              class="ml-1"
            >
              <b-avatar
                size="38"
                :variant="item.color"
                rounded
              >
                <feather-icon
                  size="20"
                  :icon="item.icon"
                />
              </b-avatar>
            </b-media-aside>
          </b-media>
        </div>
        <vue-apex-charts
          :key="chartKey"
          type="bar"
          height="40"
          :options="barChartOptions"
          :series="chartSeries"
        />
      </div>
    </template>
    <div
      v-else
      class="text-center py-2 text-muted mt-auto"
    >
      No data available
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BCardText, BMedia, BMediaAside, BMediaBody, BAvatar,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import LoadingContainer from '@/views/components/LoadingContainer.vue'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardText,
    BMedia,
    BMediaAside,
    BMediaBody,
    BAvatar,
    VueApexCharts,
    LoadingContainer,
  },
  props: {
    data: {
      type: [Object, Array],
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Overview',
    },
    subtitle: {
      type: String,
      default: 'Distribution',
    },
    dataType: {
      type: String,
      default: 'aim', // 'aim' or 'capital'
    },
  },
  data() {
    return {
      chartKey: 0,
    }
  },
  computed: {
    chartSeries() {
      const dataArray = Array.isArray(this.data) ? this.data : (this.data?.data || [])

      if (!dataArray || dataArray.length === 0) return []

      const series = dataArray.map(item => ({
        name: this.getItemName(item),
        data: [Number(item.numberOfProducts || item.count) || 0],
      }))

      return series
    },
    hasData() {
      const dataArray = Array.isArray(this.data) ? this.data : (this.data?.data || [])
      return dataArray && dataArray.length > 0
    },
    itemLabels() {
      const dataArray = Array.isArray(this.data) ? this.data : (this.data?.data || [])
      if (!dataArray || dataArray.length === 0) return []

      // Return first two items with name, count, icon, color, and filter data
      return dataArray.slice(0, 2).map(item => {
        const itemName = this.getItemName(item)
        const { icon, color } = this.getIconAndColor(itemName)

        // Determine filter category and value based on dataType
        const filterCategory = this.dataType
        let filterValue = itemName

        if (this.dataType === 'aim') {
          filterValue = item.aimType
        } else if (this.dataType === 'capital') {
          filterValue = item.capitalType
        }

        return {
          name: itemName,
          count: Number(item.numberOfProducts || item.count) || 0,
          icon,
          color,
          filterCategory,
          filterValue,
        }
      })
    },
    barChartOptions() {
      // Generate colors array from itemLabels to match icon colors
      const barColors = this.itemLabels.map(item => this.getBarColor(item.color))

      return {
        chart: {
          type: 'bar',
          height: 40,
          stacked: true,
          stackType: '100%',
          toolbar: {
            show: false,
          },
          sparkline: {
            enabled: true,
          },
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              this.handleBarClick(config.seriesIndex)
            },
          },
        },
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.1,
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '20%',
            borderRadius: 8,
            borderRadiusApplication: 'around',
          },
        },
        colors: barColors.length > 0 ? barColors : ['#1a73e8', '#28c76f'],
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        grid: {
          show: false,
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
        },
        xaxis: {
          categories: [''],
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          show: false,
        },
        tooltip: {
          y: {
            formatter(val) {
              return `${val} products`
            },
          },
        },
      }
    },
  },
  watch: {
    data: {
      handler() {
        this.chartKey += 1
      },
      deep: true,
    },
  },
  methods: {
    handleItemClick(item) {
      this.$router.push({
        name: 'current-products',
        params: {
          category: item.filterCategory,
          value: item.filterValue,
        },
      })
    },
    handleBarClick(seriesIndex) {
      // seriesIndex corresponds to the item in itemLabels array
      if (this.itemLabels[seriesIndex]) {
        this.handleItemClick(this.itemLabels[seriesIndex])
      }
    },
    getItemName(item) {
      if (this.dataType === 'aim') {
        return item.aim || item.aimType || 'Unknown'
      }
      if (this.dataType === 'capital') {
        return item.capital || item.capitalType || 'Unknown'
      }
      return item.name || 'Unknown'
    },
    getIconAndColor(itemName) {
      const nameLower = (itemName || '').toLowerCase()

      // Aim-specific mappings
      if (nameLower.includes('income')) {
        return { icon: 'PieChartIcon', color: 'light-success' }
      }
      if (nameLower.includes('growth')) {
        return { icon: 'TrendingUpIcon', color: 'light-primary' }
      }

      // Capital-specific mappings
      if (nameLower.includes('protected') || nameLower.includes('capital protected')) {
        return { icon: 'ShieldIcon', color: 'light-success' }
      }
      if (nameLower.includes('at risk') || nameLower.includes('capital at risk')) {
        return { icon: 'AlertTriangleIcon', color: 'light-danger' }
      }

      // Default fallback
      return { icon: 'TargetIcon', color: 'light-info' }
    },
    getBarColor(variantColor) {
      // Convert Bootstrap variant colors to hex colors for ApexCharts
      const colorMap = {
        'light-primary': '#1a73e8',
        'light-success': '#28c76f',
        'light-danger': '#ea5455',
        'light-warning': '#ff9f43',
        'light-info': '#00cfe8',
        primary: '#1a73e8',
        success: '#28c76f',
        danger: '#ea5455',
        warning: '#ff9f43',
        info: '#00cfe8',
      }
      return colorMap[variantColor] || '#1a73e8'
    },
  },
}
</script>

<style scoped>
.versus-overview-card ::v-deep .card-body {
  padding-bottom: 1rem !important;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.clickable-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 0.5rem;
  margin: -0.5rem;
  border-radius: 0.357rem;
}

.clickable-item:hover {
  background-color: rgba(26, 115, 232, 0.08);
}

.versus-overview-card ::v-deep .apexcharts-bar-area {
  cursor: pointer;
}
</style>
